// Smart recipient parsing for the Share popup's single input box.
// Accepts a free-text blob (comma / newline / semicolon / space separated) and
// classifies each entry as an email or a phone number, with validation. Phones
// are normalized toward E.164 (US-default when no country code is given).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizePhone(raw) {
  const cleaned = (raw || '').replace(/[^\d+]/g, '');
  if (!cleaned) return '';
  if (cleaned.startsWith('+')) return cleaned;
  const digits = cleaned.replace(/\D/g, '');
  if (digits.length === 10) return '+1' + digits;            // bare US 10-digit
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits;
  return '+' + digits;
}

// Classify a single token → { value, type: 'email' | 'phone' | 'invalid' }.
export function classifyRecipient(raw) {
  const value = (raw || '').trim();
  if (!value) return { value, type: 'invalid' };
  if (EMAIL_RE.test(value)) return { value, type: 'email' };
  const digits = value.replace(/[\s\-().]/g, '');
  if (/^\+?\d{10,15}$/.test(digits)) {
    return { value: normalizePhone(digits), type: 'phone', raw: value };
  }
  return { value, type: 'invalid' };
}

// Parse a free-text blob into a de-duplicated list of classified recipients.
export function parseRecipients(text) {
  const seen = new Set();
  const out = [];
  for (const token of (text || '').split(/[,;\n\t]+/)) {
    const t = token.trim();
    if (!t) continue;
    const r = classifyRecipient(t);
    const key = `${r.type}:${r.value.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

// Only the deliverable (valid) recipients, in the {type, value} shape the
// send-invite endpoint expects.
export function validRecipients(text) {
  return parseRecipients(text)
    .filter((r) => r.type === 'email' || r.type === 'phone')
    .map((r) => ({ type: r.type, value: r.value }));
}
